import React, { useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { ChildProvider } from './context/ChildProvider';
import AppContent from './AppContent';

import OnboardingScreen from './screens/OnboardingScreen';
import AuthScreen from './screens/AuthScreen';

// Import Components
import { LoadingScreen } from './components/UI';
import PWAInstallPrompt from './components/PWAInstallPrompt';

// --- Custom Hook for Authentication and Full User Profile ---
const useAuthWithRole = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                if (currentUser.isAnonymous) {
                    setUser({ uid: currentUser.uid, role: 'Tamu', isAnonymous: true });
                } else {
                    const userDocRef = doc(db, 'users', currentUser.uid);
                    const userDoc = await getDoc(userDocRef);
                    if (userDoc.exists()) {
                        setUser({ uid: currentUser.uid, ...userDoc.data() });
                    } else {
                        setUser({ uid: currentUser.uid, email: currentUser.email, role: 'Orang Tua' });
                    }
                }
            } else {
                setUser(null);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    return { user, loading };
};

export default function App() {
    const { user, loading: authLoading } = useAuthWithRole();
    const [page, setPage] = useState('home');
    const [subPage, setSubPage] = useState(null);
    const [selectedChild, setSelectedChild] = useState(null);
    const [data, setData] = useState(null);
    const [showOnboarding, setShowOnboarding] = useState(!localStorage.getItem('onboardingCompleted'));
    const [toast, setToast] = useState(null);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const navigate = useCallback((pageName, child = null, pageData = null) => {
        const mainPages = ['home', 'assessmentHub', 'stimulation', 'education', 'profile'];
        if (mainPages.includes(pageName)) {
            setPage(pageName);
            setSubPage(null);
        } else {
            setSubPage(pageName);
        }
        setSelectedChild(child);
        setData(pageData);
    }, []);

    const handleBack = () => {
        setSubPage(null);
    };
    
    const handleLogout = async () => {
        await signOut(auth);
        setPage('home');
        setSubPage(null);
    };

    const handleOnboardingFinish = () => {
        localStorage.setItem('onboardingCompleted', 'true');
        setShowOnboarding(false);
    };

    if (authLoading) return <LoadingScreen message="Memverifikasi sesi Anda..." />;
    if (showOnboarding) return <OnboardingScreen navigate={handleOnboardingFinish} />;
    if (!user) return <AuthScreen />;
    
    return (
        <ChildProvider user={user}>
            <AppContent
                user={user}
                page={page}
                setPage={setPage}
                subPage={subPage}
                setSubPage={setSubPage}
                selectedChild={selectedChild}
                setSelectedChild={setSelectedChild}
                data={data}
                setData={setData}
                showToast={showToast}
                navigate={navigate}
                handleBack={handleBack}
                handleLogout={handleLogout}
                toast={toast}
                setToast={setToast}
            />
            <PWAInstallPrompt />
        </ChildProvider>
    );
}
